/* eslint-disable no-nested-ternary */
import { Body1, Header1, LabelSeparator } from '@flatsy/core/MaterialUi/Typo'
import { FormValue } from '@flatsy/types/Applications'
import { Button } from '@material-ui/core'
import React, { useContext, useEffect } from 'react'
import { FormContext, ReturnContextProps } from 'src/containers/Qualification/Context'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { TextError } from '@flatsy/core/Form/ReactFormHooks/Field/TextError'
import FlatsyButtonsField from '@flatsy/core/Form/ReactFormHooks/Field/Buttons'
import FlatsyTextField from '@flatsy/core/Form/ReactFormHooks/Field/Text'
import i18n from '@flatsy/utils/translations'
import { useFormStyles } from '../../Style'
import { SharedProps } from '../../Type'
import useStyles from '../Style'
import { ProjectSalesData } from './Type'
import { buyingReason, financeOrigin, selling } from './Utils'

const ProjectSale: React.FC<SharedProps> = ({ withOutApplication, renderMobileFormButton }) => {
  const formClasses = useFormStyles()
  const classes = useStyles()
  const {
    wizardContext: { context },
    saveApplicationInRedux,
    saveApplicationAndContinue,
  } = useContext(FormContext) as ReturnContextProps
  const { application } = context

  const schema = yup.object({
    buying_reason: yup.string().required().typeError(TextError.empty),
    is_selling: yup.string().required().typeError(TextError.empty),
    finance_origin: yup.string().required().typeError(TextError.empty),
    loan_amount: yup
      .string()
      .nullable()
      .when('finance_origin', {
        is: (val: string) => val === FormValue.financeOrigin.BNK,
        then: yup.string().required().typeError(TextError.empty),
      }),
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitSuccessful, isValid, isDirty },
  } = useForm<ProjectSalesData>({
    mode: 'all',
    resolver: yupResolver(schema),
  })

  const finance_origin = watch('finance_origin')

  useEffect(() => {
    if (application) {
      reset({
        buying_reason: application.buying_reason,
        is_selling:
          application.is_selling === true
            ? 'true'
            : application.is_selling === false
            ? 'false'
            : undefined,
        finance_origin: application.finance_origin,
        loan_amount: application.loan_amount,
      })
    }
  }, [application])

  useEffect(() => {
    if (finance_origin) {
      setValue('loan_amount', undefined)
    }
  }, [finance_origin])

  if (!application) {
    return null
  }

  const onSubmit = async (values: ProjectSalesData): Promise<undefined> => {
    if (withOutApplication) {
      saveApplicationInRedux({
        ...values,
        is_selling: values.is_selling === 'true',
      })
    } else {
      saveApplicationAndContinue({
        ...values,
        is_selling: values.is_selling === 'true',
      })
    }
    return undefined
  }

  return (
    <div className={formClasses.container}>
      <div className={classes.intro}>
        <Header1 className={classes.introTitle} lowerCase align="center">
          {i18n.t('welcome')}
        </Header1>
        <Body1 align="center">
          {i18n.t('in_order_to_schedule_a_tour_can_you_tell_us_more_about_your_purchase_project')}
        </Body1>
        <div className={classes.border} />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <LabelSeparator className={formClasses.smallMarginLabel}>
          {i18n.t('what_is_your_reason_for_buying')}
        </LabelSeparator>
        <Controller
          name="buying_reason"
          control={control}
          render={({ field, fieldState }) => (
            <FlatsyButtonsField
              {...field}
              {...fieldState}
              isSubmitSuccessful={isSubmitSuccessful}
              options={buyingReason()}
            />
          )}
        />
        <LabelSeparator className={formClasses.smallMarginLabel}>
          {i18n.t('do_you_have_a_property_for_sale')}
        </LabelSeparator>
        <Controller
          name="is_selling"
          control={control}
          render={({ field, fieldState }) => (
            <FlatsyButtonsField
              {...field}
              {...fieldState}
              isSubmitSuccessful={isSubmitSuccessful}
              options={selling()}
            />
          )}
        />
        <LabelSeparator className={formClasses.smallMarginLabel}>
          {i18n.t('how_do_you_intend_to_finance_this_purchase')}
        </LabelSeparator>
        <Controller
          name="finance_origin"
          control={control}
          render={({ field, fieldState }) => (
            <FlatsyButtonsField
              {...field}
              {...fieldState}
              isSubmitSuccessful={isSubmitSuccessful}
              options={financeOrigin()}
            />
          )}
        />
        {finance_origin === FormValue.financeOrigin.BNK && (
          <Controller
            name="loan_amount"
            control={control}
            render={({ field, fieldState }) => (
              <FlatsyTextField
                {...field}
                {...fieldState}
                isSubmitSuccessful={isSubmitSuccessful}
                type="number"
                label={i18n.t('bank_loan_amount')}
              />
            )}
          />
        )}
        {!renderMobileFormButton ? (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            type="submit"
            className={formClasses.submit}
            disabled={!isValid}
          >
            {i18n.t('continue')}
          </Button>
        ) : (
          renderMobileFormButton(isDirty, !isValid)
        )}
      </form>
    </div>
  )
}

export default ProjectSale
